import {
  get,
  isNil,
  replace,
  toLower,
  upperFirst,
} from 'lodash';
import { DateTime } from 'luxon';
import moment from 'moment';

import { organizationTypeId, programForUnitType, unitTypes } from '@shared';

import { PaymentSources, PaymentTypes } from '../../types/enums';
import type {
  CompletePersonProfile,
  OrgProfile,
  PaymentLogEntry,
  PaymentLogRequest,
} from '../../types/esb';
import { API_DATE_FORMAT } from './constants';
import type {
  PaymentLogDates,
  TransformFormToRequestValues,
  Unit,
} from './types';

export const transformPersonProfileToUnits = (
  personProfile: CompletePersonProfile,
): Unit[] => {
  const organizationPositions = get(personProfile, 'organizationPositions', []);

  return organizationPositions.map(
    ({
      sbOrganizationId: unitId,
      organizationName,
      charterOrgName,
      unitType: rawUnitType,
      organizationGuid,
    }) => {
      const name = `${organizationName} ${charterOrgName}`;
      const unitType = upperFirst(
        toLower(rawUnitType),
      ) as keyof typeof unitTypes;
      const program = get(programForUnitType, unitType);

      return {
        name,
        program,
        unitId: Number(unitId),
        unitType,
        organizationGuid,
      };
    },
  );
};

export const transformOrgProfileToUnits = (orgProfiles: OrgProfile[]): Unit[] =>
  orgProfiles
    .filter(
      orgProfile => orgProfile.organizationTypeId === organizationTypeId.UNIT,
    )
    .map(orgProfile => {
      const {
        sbOrganizationId: unitId,
        name,
        charter,
        type,
        organizationGuid,
      } = orgProfile;
      const { communityOrganizationName } = charter ?? {
        communityOrganizationName: '',
      };
      const displayName = `${name} ${communityOrganizationName}`;
      const unitType = upperFirst(toLower(type)) as keyof typeof unitTypes;
      const program = get(programForUnitType, unitType);

      return {
        name: displayName,
        program,
        unitId: Number(unitId),
        unitType,
        organizationGuid,
      };
    });

export const transformFormToRequest = (
  values: TransformFormToRequestValues,
  log?: PaymentLogEntry,
): PaymentLogRequest => {
  const {
    amount: rawAmount,
    applyPaymentLogId,
    calendarEventId,
    category,
    comments: notes,
    date,
    dueDate,
    name: description,
    transactionId,
    type: paymentType,
    unitId,
    userId,
  } = values;

  const amount = [PaymentTypes.CHARGE, PaymentTypes.DEBIT].includes(paymentType)
    ? Number(replace(rawAmount, ',', '.')) * -1
    : Number(replace(rawAmount, ',', '.'));

  const hasDifferentDueDate = log?.paymentDueDate
    ? !moment(log.paymentDueDate).isSame(dueDate, 'day')
    : true;
  const includeDueDate = !isNil(dueDate) && hasDifferentDueDate;

  const request = {
    source: PaymentSources.USER,
    userId,
    amount,
    applyPaymentLogId,
    ...(!isNil(calendarEventId) && { calendarEventId }),
    ...(!isNil(category) && { category }),
    description,
    logDate: date.format(API_DATE_FORMAT),
    notes,
    ...(includeDueDate && {
      paymentDueDate: dueDate.format(API_DATE_FORMAT),
    }),
    paymentType,
    transactionId,
    unitId,
  };

  return request;
};

export const getDateRange = (filters: { dates: PaymentLogDates }) => {
  if (isNil(filters.dates.fromDate)) return '';
  const start = DateTime.fromISO(filters.dates.fromDate).toFormat('MM/dd/yyyy');
  const end = DateTime.fromISO(filters.dates.toDate).toFormat('MM/dd/yyyy');
  return `${start} - ${end}`;
};

export const checkIsToday = (date: string) =>
  DateTime.fromISO(date).hasSame(DateTime.now(), 'day');

export const openPaymentLogsInNewTab = ({
  userId,
  personGuid,
}: {
  userId: number;
  personGuid: string;
}) => {
  window.open(
    `/paymentLogs/${userId}?personGuid=${personGuid}`,
    '_blank',
    'noopener,noreferrer',
  );
};
